import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { groupNodesByCategory } from "../../utils/workflowNodes";
import NodeIcon from "./NodeIcon";

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: 100%;
  overflow-y: auto;
  color: var(--main-font-color);
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 4px 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #a0a6b1;
  position: sticky;
  top: 0;
  background-color: var(--main-bg);
  z-index: 1;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.color ?? "var(--button-bg)"};
  flex-shrink: 0;
`;

const Count = styled.span`
  font-weight: 400;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(props) => (props.selected ? "var(--button-bg)" : "transparent")};
  border-radius: 6px;
  background-color: ${(props) => (props.selected ? "var(--content-item-bg)" : "transparent")};
`;

const ItemHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 8px;
  border: none;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
  text-align: left;
  font-family: inherit;

  &:hover {
    background-color: var(--content-item-bg);
    border-radius: 6px;
  }
`;

const ItemTitle = styled.span`
  flex: 1;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.25;
`;

const Chevron = styled.span`
  font-size: 10px;
  color: #a0a6b1;
  transform: ${(props) => (props.open ? "rotate(180deg)" : "none")};
  transition: transform 0.12s ease;
`;

const ItemBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 2px 10px 10px;
  border-top: 1px solid var(--content-item-border-color);
  margin: 0 8px 6px;
  font-size: 12px;
`;

const Description = styled.div`
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--main-font-color);
  opacity: 0.9;
`;

const Notes = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: #a0a6b1;
  border-left: 2px solid var(--content-item-border-color);
  padding-left: 8px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #a0a6b1;
`;

const PortRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
`;

const PortDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${(props) => (props.kind === "source" ? "#059669" : "#2563eb")};
  flex-shrink: 0;
`;

const ParamRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 11px;
  font-family: monospace;
`;

const ParamName = styled.span`
  font-weight: 700;
`;

const ParamType = styled.span`
  color: #a0a6b1;
`;

const Muted = styled.span`
  font-size: 11px;
  font-style: italic;
  color: #a0a6b1;
`;

/**
 * The left-hand node catalog for the workflow-nodes page: grouped by category, each entry is a
 * compact row that expands to show its description, notes, ports and parameters.
 */
export const WorkflowNodeList = ({ nodes = [], categories = [], selectedType = null, onSelect = () => { } }) => {
  const [expanded, setExpanded] = useState(null);

  const groups = useMemo(() => groupNodesByCategory(nodes, categories), [nodes, categories]);

  return (
    <ListWrapper>
      {groups.map((group) => (
        <Group key={group.category.id}>
          <GroupHeader>
            <Dot color={group.category.color} />
            {group.category.label}
            <Count>{group.nodes.length}</Count>
          </GroupHeader>

          {group.nodes.map((node) => {
            const isOpen = expanded === node.type;
            const isSelected = selectedType === node.type;

            return (
              <Item key={node.type} selected={isSelected}>
                <ItemHeader
                  type="button"
                  onClick={() => {
                    setExpanded(isOpen ? null : node.type);
                    onSelect(node.type);
                  }}
                >
                  <NodeIcon icon={node.icon} size={16} color={node.categoryColor ?? group.category.color ?? "currentColor"} />
                  <ItemTitle>{node.name}</ItemTitle>
                  <Chevron open={isOpen}>▾</Chevron>
                </ItemHeader>

                {isOpen && (
                  <ItemBody>
                    {node.description ? <Description>{node.description}</Description> : null}
                    {node.notes ? <Notes>{node.notes}</Notes> : null}

                    <Section>
                      <SectionLabel>Inputs</SectionLabel>
                      {node.handles.inputs.length > 0 ? (
                        node.handles.inputs.map((handle) => (
                          <PortRow key={handle.id}><PortDot kind="target" />{handle.label}</PortRow>
                        ))
                      ) : (
                        <Muted>none</Muted>
                      )}
                      {node.handles.dynamicInputs ? <Muted>dynamic</Muted> : null}
                    </Section>

                    <Section>
                      <SectionLabel>Outputs</SectionLabel>
                      {node.handles.outputs.length > 0 ? (
                        node.handles.outputs.map((handle) => (
                          <PortRow key={handle.id}><PortDot kind="source" />{handle.label}</PortRow>
                        ))
                      ) : (
                        <Muted>none</Muted>
                      )}
                      {node.handles.dynamicOutputs ? <Muted>dynamic</Muted> : null}
                    </Section>

                    {node.parameters.length > 0 ? (
                      <Section>
                        <SectionLabel>Parameters</SectionLabel>
                        {node.parameters.map((parameter) => (
                          <ParamRow key={parameter?.name}>
                            <ParamName>{parameter?.name}</ParamName>
                            <ParamType>{parameter?.type ?? parameter?.control}</ParamType>
                            {parameter?.required ? <span style={{ color: "#c0392b" }}>*</span> : null}
                            {parameter?.advanced ? <Muted>advanced</Muted> : null}
                          </ParamRow>
                        ))}
                      </Section>
                    ) : null}
                  </ItemBody>
                )}
              </Item>
            );
          })}
        </Group>
      ))}
    </ListWrapper>
  );
};

export default WorkflowNodeList;
